import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, RefreshCw, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { WorkQueueRow } from '@/components/WorkQueueRow';

const TYPE_OPTS = [
  ['all', 'All Types'],
  ['violation_hearing', 'Violation Hearings'],
  ['permit_expiring', 'Permit Expirations'],
  ['dob_expiring', 'DOB Filings'],
  ['inspection_upcoming', 'FDNY Inspections'],
  ['compliance_item', 'Compliance'],
  ['cotco_milestone', 'CO/TCO'],
  ['task_due', 'Tasks'],
  ['proposal_pending', 'Proposals'],
];

const URGENCY_OPTS = [
  ['all', 'All Urgencies'],
  ['critical', 'Critical'],
  ['warning', 'Warning'],
  ['upcoming', 'Upcoming'],
];

export default function WorkQueuePage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [urgency, setUrgency] = useState('all');
  const [type, setType] = useState('all');
  const [search, setSearch] = useState('');

  const fetchQueue = () => {
    setLoading(true);
    api.get('/work-queue')
      .then(r => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchQueue(); }, []);

  const dismissItem = async (item) => {
    setItems(prev => prev.filter(i => i.id !== item.id));
    try {
      await api.post('/work-queue/dismiss', { source_module: item.source_module, source_id: item.id, urgency: item.urgency });
      window.dispatchEvent(new Event('workqueue-changed'));
      toast.success('Item dismissed');
    } catch {
      toast.error('Could not dismiss item');
      fetchQueue();
    }
  };

  const all = Array.isArray(items) ? items : [];
  const attentionCount = all.filter(i => i.urgency === 'critical' || i.urgency === 'warning').length;

  const filtered = all.filter(i => {
    if (urgency !== 'all' && i.urgency !== urgency) return false;
    if (type !== 'all' && i.type !== type) return false;
    const q = search.toLowerCase().trim();
    if (q && !((i.title || '').toLowerCase().includes(q) || (i.subtitle || '').toLowerCase().includes(q))) return false;
    return true;
  });

  return (
    <div className="space-y-4 animate-fade-in" data-testid="work-queue-page">
      <div>
        <h1 className="font-semibold tracking-tight" style={{ fontSize: '18px', color: '#010A21' }}>Work Queue</h1>
        <p style={{ fontSize: '12px', color: '#031333' }} data-testid="work-queue-count">{attentionCount} items need attention</p>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={urgency} onValueChange={setUrgency}>
          <SelectTrigger className="w-40 bg-card border-border text-sm" data-testid="wq-urgency-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card border-border">
            {URGENCY_OPTS.map(([v, l]) => <SelectItem key={v} value={v} className="text-sm">{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-48 bg-card border-border text-sm" data-testid="wq-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card border-border">
            {TYPE_OPTS.map(([v, l]) => <SelectItem key={v} value={v} className="text-sm">{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search work queue..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border" data-testid="wq-search" />
        </div>
        <div className="ml-auto">
          <Button variant="outline" size="icon" className="border-border" onClick={fetchQueue} disabled={loading} title="Refresh" data-testid="wq-refresh-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Work queue list */}
      {loading ? (
        <p className="text-sm text-muted-foreground py-8 text-center">Loading work queue...</p>
      ) : all.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16" data-testid="wq-empty-state">
          <CheckCircle2 className="mb-3" style={{ height: '40px', width: '40px', color: '#16A34A' }} />
          <p style={{ fontSize: '14px', color: '#031333' }}>No items need attention.</p>
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center" data-testid="wq-no-matches">No items match the current filters.</p>
      ) : (
        <div data-testid="work-queue-list">
          {filtered.map(item => (
            <WorkQueueRow key={item.id} item={item} onGo={(u) => navigate(u)} onDismiss={dismissItem} />
          ))}
        </div>
      )}
    </div>
  );
}
